import { addDays, daysBetween } from '@fd/finance-core';
import type { Category } from '@fd/schemas';
import { ArrowDownLeft, ArrowRight, CalendarClock } from 'lucide-react';
import { today, useCategories, useRecurring, type RecurringItem } from '../app/data.ts';
import { useT } from '../app/i18n.tsx';
import { hrefFor } from '../app/router.tsx';
import { MerchantIcon } from '../ui/BrandLogo.tsx';
import { Card, EmptyState, Money } from '../ui/primitives.tsx';

/** Icon and colour of a recurring payment: its category's when known, loans always as debt. */
export function patternLook(p: RecurringItem, categories: readonly Category[]): { icon: string; color: string } {
  const category = categories.find((c) => c.id === p.categoryId);
  if (p.shownKind === 'DEBT' || p.shownKind === 'TRANSFER' || !category) return KIND_ICON[p.shownKind];
  return { icon: category.icon ?? KIND_ICON[p.shownKind].icon, color: category.color ?? KIND_ICON[p.shownKind].color };
}

const KIND_ICON: Record<RecurringItem['shownKind'], { icon: string; color: string }> = {
  SUBSCRIPTION: { icon: 'repeat', color: 'var(--series-7)' },
  BILL: { icon: 'receipt', color: 'var(--series-1)' },
  DEBT: { icon: 'landmark', color: 'var(--fill-debt)' },
  INCOME: { icon: 'briefcase', color: 'var(--fill-income)' },
  TRANSFER: { icon: 'transfer', color: 'var(--series-7)' },
  OTHER: { icon: 'repeat', color: 'var(--fill-neutral)' },
};

/**
 * "Due in 3 days", "Overdue" … with the colour of urgency (text, never colour alone). The date is
 * already on a working day; a day after it the payment may simply not have reached us yet (the
 * bank's data comes once a day), so it is late only from the day after that.
 */
export function DueLabel({ date }: { date: string }) {
  const t = useT();
  const days = daysBetween(today(), date);
  if (days === -1) return <span className="due due--soon">{t.t('recurring.awaitingBank')}</span>;
  if (days < 0) return <span className="due due--overdue">{t.t('recurring.overdue')}</span>;
  if (days === 0) return <span className="due due--soon">{t.t('recurring.dueToday')}</span>;
  if (days === 1) return <span className="due due--soon">{t.t('recurring.dueTomorrow')}</span>;
  return <span className={`due${days <= 3 ? ' due--soon' : ''}`}>{t.plural('recurring.dueIn', days)}</span>;
}

/**
 * The next two weeks of what comes and goes regularly (spec §50–51): bills, subscriptions, loan
 * repayments, standing orders and pay, overdue first. Money coming in reads "+" and green, so the
 * two directions tell apart at a glance; the footer adds up what goes out.
 */
export function UpcomingPayments({ className }: { className?: string }) {
  const t = useT();
  const { items, loading } = useRecurring();
  const categories = useCategories();
  const horizon = addDays(today(), 14);
  // Money arriving from the owner's own accounts is not news; pay and standing orders out are.
  const due = items
    .filter(
      (p) =>
        p.active &&
        p.status !== 'DISMISSED' &&
        !(p.direction === 'IN' && p.shownKind === 'TRANSFER') &&
        (p.nextDate <= horizon || p.overdue),
    )
    .sort((a, b) => (a.nextDate < b.nextDate ? -1 : a.nextDate > b.nextDate ? 1 : 0));
  const out = new Map<string, bigint>();
  for (const p of due) if (p.direction === 'OUT') out.set(p.currency, (out.get(p.currency) ?? 0n) + p.typicalAmountMinor);

  return (
    <Card
      className={`upcoming-card ${className ?? ''}`}
      title={t.t('recurring.upcoming')}
      subtitle={t.t('overview.next14')}
      flush
      action={
        <a className="card__link" href={hrefFor({ page: 'recurring' })}>
          {t.t('overview.viewCalendar')} <ArrowRight aria-hidden="true" />
        </a>
      }
    >
      {loading ? null : due.length === 0 ? (
        <EmptyState icon={<CalendarClock size={20} />} title={t.t('recurring.upcomingEmpty')} text={t.t('recurring.upcomingHint')} />
      ) : (
        <>
          <ul className="upcoming-card__list">
            {due.slice(0, 6).map((p) => {
              const look = patternLook(p, categories);
              const incoming = p.direction === 'IN';
              return (
                <li key={p.signature} className={`upcoming-card__row${incoming ? ' upcoming-card__row--in' : ''}`}>
                  <MerchantIcon texts={[p.displayName, p.name]} icon={look.icon} color={look.color} />
                  <span className="upcoming-card__what">
                    <span className="upcoming-card__name">{p.displayName}</span>
                    <span className="upcoming-card__when">
                      <DueLabel date={p.nextDate} />
                      <span>· {t.t(`recurring.kind.${p.shownKind}`)}</span>
                    </span>
                  </span>
                  <span className={`upcoming-card__amount${incoming ? ' fg-income' : ''}`}>
                    {incoming && <ArrowDownLeft aria-hidden="true" />}
                    {p.fixedAmount ? (
                      <Money minor={p.typicalAmountMinor} currency={p.currency} options={incoming ? { signDisplay: 'always' } : undefined} />
                    ) : (
                      <span className="money">
                        {t.t('recurring.about', { amount: t.money({ minor: p.typicalAmountMinor, currency: p.currency }, incoming ? { signDisplay: 'always' } : undefined) })}
                      </span>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
          <div className="card__footer">
            <span>{due.length > 6 ? t.plural('recurring.more', due.length - 6) : t.t('overview.goingOut')}</span>
            <strong className="amount-text">{[...out.entries()].map(([currency, minor]) => t.money({ minor, currency })).join(' · ') || '—'}</strong>
          </div>
        </>
      )}
    </Card>
  );
}
